import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';

export interface TablePaginationProps {
  /** 1-based current page */
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** Plural noun shown in count label, e.g. "candidates", "jobs". Defaults to "items". */
  entityLabel?: string;
  /** When false, hides the per-page selector. Default true. */
  showPageSizeSelect?: boolean;
}

const PAGE_SIZES = [5, 10, 25, 50, 100] as const;

const navBtn =
  'flex h-7 w-7 items-center justify-center rounded border border-[var(--vscode-border-primary)] vscode-text-secondary transition-colors hover:vscode-text-primary hover:bg-[var(--vscode-bg-hover)] disabled:opacity-30 disabled:cursor-not-allowed';

export function TablePagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  entityLabel = 'items',
  showPageSizeSelect = true,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  return (
    <div className="ggj-data-pagination flex w-full items-center justify-between gap-4 px-4 py-2.5">
      {/* Left: range + entity label */}
      <span className="text-[12px] vscode-text-tertiary tabular-nums select-none">
        {totalItems === 0
          ? `0 ${entityLabel}`
          : `${start}–${end} of ${totalItems} ${entityLabel}`}
      </span>

      {/* Right: page size selector + first/prev/next/last */}
      <div className="flex items-center gap-2">
        {showPageSizeSelect && onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
            }}
            className="h-7 rounded border border-[var(--vscode-border-primary)] bg-transparent px-1.5 text-[12px] vscode-text-secondary cursor-pointer"
            aria-label="Rows per page"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        )}

        <div className="flex items-center gap-1">
          <button
            type="button"
            className={navBtn}
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            aria-label="First page"
          >
            <ChevronsLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            className={navBtn}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            disabled={page <= 1}
            aria-label="Previous page"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            className={navBtn}
            onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            aria-label="Next page"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            className={navBtn}
            onClick={() => onPageChange(totalPages)}
            disabled={page >= totalPages}
            aria-label="Last page"
          >
            <ChevronsRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
